import {
  ArrowLeftRight,
  BadgeCheck,
  BarChart3,
  Bell,
  BookOpen,
  Boxes,
  Briefcase,
  Building2,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  FileMinus2,
  FileStack,
  FileText,
  FolderKanban,
  Gauge,
  GitBranch,
  Globe,
  Hash,
  KeyRound,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Package,
  Paintbrush,
  Phone,
  PhoneCall,
  Plug,
  Radio,
  Receipt,
  ReceiptText,
  RefreshCw,
  ListTodo,
  Repeat,
  Route,
  Settings,
  Shield,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Star,
  Tag,
  Tags,
  Timer,
  TrendingUp,
  Truck,
  UserPlus,
  Users,
  Users2,
  Wallet,
  Wrench,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { REVIEW_POLICY_VIEW_PERMISSIONS } from '@/types/reminder-policy';
import { PURCHASING_FLAGS } from '@notifi/shared-types/feature-flags';

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  sectionId?: string;
  exact?: boolean;
  activePaths?: string[];
  children?: NavItem[];
  /** If set, only shown when user has this permission */
  requiredPermission?: string;
  /** If set, shown when the user has ANY of these permissions. Use for a route
   *  whose backend accepts more than one permission (e.g. company:view OR
   *  company:edit), so the nav matches the API's own gate. */
  requiredAnyPermission?: string[];
  /** FIX-myday — if set, only shown when the user is linked to a technician
   *  (auth-store technicianId != null). Evaluated at render time. */
  requiresTechnicianLink?: true;
  /** Hide this item when the actor's scope on the given permission is OWN.
   *  Used to keep the office scheduling cockpit (Dispatch) out of a field
   *  tech's nav — an OWN `calendar:view` scope means "only my own schedule". */
  hiddenForOwnScopeOf?: string;
  /** Only shown when the actor holds this permission at ALL scope — for surfaces
   *  that show everyone's records at once (live call monitoring). */
  requiresAllScopeOf?: string;
  /** If set, only shown when tenant-wide costing reports are enabled. */
  requiresCostingReporting?: true;
  /** If set, only shown while the tenant has this feature flag on (PLAT-FF-1). */
  requiredFlag?: string;
  /** Optional group label for visual grouping in the sidebar */
  group?: string;
  /** Hide routes whose content cannot be rendered safely under these masks. */
  hiddenForFieldMasks?: string[];
  /** Extra terms the command palette matches on, e.g. a page's former name
   *  after a rename/move so old habits ("Call Logs", "Requests") still find it. */
  aliases?: string[];
}

export interface NavSection {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

/** Context that gates a nav item's visibility beyond the raw permission set. */
export interface NavVisibilityContext {
  /** Linked technician id (auth-store), or null when not a technician. */
  technicianId: string | null;
  /** Actor's scope per permission key (auth-store permissionScopes). Absent →
   *  no scope-based gating (office default). */
  permissionScopes?: Record<string, string>;
  /** Tenant costing-reporting toggle from contract costing settings. */
  costingReportingEnabled?: boolean;
  /** The tenant's feature flags as the store holds them. Absent → every gated item hidden. */
  flags?: Record<string, boolean>;
  fieldMasks?: readonly string[];
}

/**
 * Single source of truth for whether a nav item is visible: the required
 * permission AND (FIX-myday) the technician link when the item demands it.
 * Evaluated at render time so a late link/permission change reflects on the
 * next render. Used by every nav surface (icon rail, panel, mobile, palette).
 */
export function isNavItemVisible(
  item: NavItem,
  permissions: string[],
  ctx: NavVisibilityContext,
): boolean {
  if (item.requiredPermission && !permissions.includes(item.requiredPermission)) {
    return false;
  }
  if (
    item.requiredAnyPermission &&
    !item.requiredAnyPermission.some((p) => permissions.includes(p))
  ) {
    return false;
  }
  if (item.requiresTechnicianLink && ctx.technicianId == null) {
    return false;
  }
  if (item.hiddenForOwnScopeOf && ctx.permissionScopes?.[item.hiddenForOwnScopeOf] === 'OWN') {
    return false;
  }
  if (item.requiresAllScopeOf && ctx.permissionScopes?.[item.requiresAllScopeOf] !== 'ALL') {
    return false;
  }
  if (item.requiresCostingReporting && ctx.costingReportingEnabled !== true) {
    return false;
  }
  if (item.requiredFlag && ctx.flags?.[item.requiredFlag] !== true) {
    return false;
  }
  if (item.hiddenForFieldMasks?.some((mask) => ctx.fieldMasks?.includes(mask))) {
    return false;
  }
  return true;
}

/**
 * Routes that still resolve but are no longer nav destinations.
 *
 * `Service calls` was removed from the Jobs section, but
 * `/dashboard/jobs/service-calls` stays live — the job-detail back-link and the
 * booking return flow both navigate to it. Without this list the sibling that
 * owned the route is gone, so `childActive`'s longest-prefix rule hands the
 * route to the shorter `/dashboard/jobs` child and the sidebar highlights
 * "All jobs" while the page header reads "Service calls". No child may claim
 * these paths; the parent section still marks, which is the honest state —
 * you are in Jobs, just not on a lens the nav lists.
 */
export const NON_NAV_ROUTES: readonly string[] = ['/dashboard/jobs/service-calls'];

/** True when `pathname` is inside a route no nav child should claim. */
export function isNonNavRoute(pathname: string): boolean {
  return NON_NAV_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const PINNED_NAV_ITEMS: NavItem[] = [
  {
    label: 'Home',
    path: '/dashboard/home',
    icon: LayoutDashboard,
    exact: true,
  },
  // The Getting started onboarding checklist (/dashboard/onboarding) is no longer
  // a sidebar item — the company logo (gated on company:edit) opens it instead.
  {
    label: 'Inbox',
    path: '/dashboard/inbox',
    icon: MessageSquare,
    requiredPermission: 'messages:view',
  },
  {
    label: 'Team chat',
    path: '/dashboard/team-chat',
    icon: MessagesSquare,
    aliases: ['internal chat', 'internal messages', 'team messages', 'call a teammate'],
    requiredPermission: 'internal_messages:view',
  },
  {
    label: 'Calls',
    path: '/dashboard/calllogs',
    icon: PhoneCall,
    aliases: ['call logs'],
    // Gate on messages:view to match the operations call-logs proxy
    // (messages:view reads / messages:edit writes) and the sibling comms nav
    // items (Inbox, Phone, SMS). The `calls` resource isn't granted to any
    // role but owner, so `calls:view` would hide this from admins/staff who
    // can otherwise use the API.
    requiredPermission: 'messages:view',
  },
  {
    label: 'Live calls',
    path: '/dashboard/live-calls',
    icon: Radio,
    aliases: ['call monitoring', 'active calls', 'listen'],
    // Everyone's calls and live transcripts: the operations API demands
    // calls:view at ALL scope, so the nav does too.
    requiredPermission: 'calls:view',
    requiresAllScopeOf: 'calls:view',
  },
  // NOT-567 — the pinned "Notifications" item is removed: it duplicated the
  // header bell (same badge, same feed). The bell in Header.tsx is the single
  // entry point; the /dashboard/notifications route still exists for deep links.
  {
    label: 'Schedule',
    // Sibling surfaces (sidebar secondary panel, like Jobs/Contacts): Dispatch
    // (operational cockpit, default) + Routes (recurring/route planning) + Calendar.
    path: '/dashboard/dispatch',
    sectionId: 'schedule',
    activePaths: [
      '/dashboard/dispatch',
      '/dashboard/routes',
      '/dashboard/calendar',
      '/dashboard/calendar/map',
      '/dashboard/board',
      '/dashboard/unscheduled',
      '/dashboard/my-day',
    ],
    icon: CalendarDays,
    requiredPermission: 'calendar:view',
    children: [
      {
        label: 'Dispatch',
        path: '/dashboard/dispatch',
        icon: Zap,
        requiredPermission: 'calendar:view',
        // The dispatch board is the flota-wide office cockpit; a field tech
        // (OWN calendar scope) is redirected away from it by the route guard,
        // so keep the dead link out of their nav too.
        hiddenForOwnScopeOf: 'calendar:view',
      },
      {
        label: 'Calendar',
        path: '/dashboard/calendar',
        icon: CalendarDays,
        requiredPermission: 'calendar:view',
      },
      {
        label: 'Routes',
        path: '/dashboard/routes',
        icon: Route,
        requiredPermission: 'calendar:view',
        // Route optimization reads `/routes/summaries`, which requires
        // dispatch_board:view — a field tech (OWN calendar scope) lacks it and
        // would only ever see an empty board, so keep Routes (like Dispatch) out
        // of their nav; the route guard redirects any direct hit to My Day.
        hiddenForOwnScopeOf: 'calendar:view',
      },
      // §9 — the field tech's own live-sharing surface. Gated by the real
      // technician link (not the role name); the permission stays as
      // defense-in-depth. Hidden for admins/office staff with no linked tech.
      {
        label: 'My Day',
        path: '/dashboard/my-day',
        icon: MapPin,
        requiredPermission: 'tracking:view',
        requiresTechnicianLink: true,
      },
    ],
  },
  {
    label: 'Jobs',
    path: '/dashboard/jobs',
    sectionId: 'jobs',
    activePaths: [
      '/dashboard/jobs',
      '/dashboard/bookings',
      '/dashboard/costing/reports',
      '/dashboard/projects',
    ],
    icon: Briefcase,
    requiredPermission: 'jobs:view',
    children: [
      {
        // NOT-567 — "All jobs" replaces the child that repeated the parent name.
        label: 'All jobs',
        path: '/dashboard/jobs',
        icon: Briefcase,
        requiredPermission: 'jobs:view',
      },
      {
        // SPEC-projects — umbrella over related jobs/estimates/invoices with
        // rollups and AIA-style progress billing. Lives under Jobs as a lens.
        label: 'Projects',
        path: '/dashboard/projects',
        icon: FolderKanban,
        aliases: ['project', 'pay applications', 'schedule of values'],
        requiredPermission: 'jobs:view',
      },
      {
        label: 'Costing Reports',
        path: '/dashboard/costing/reports',
        icon: Wallet,
        requiredPermission: 'contracts:view',
        requiresCostingReporting: true,
      },
    ],
  },
  {
    // NOT-567 — Contacts is customers-only now: Leads moved to Sales, so the
    // single remaining child repeated the parent — collapse to a leaf item.
    label: 'Contacts',
    path: '/dashboard/contacts',
    icon: Users,
    requiredPermission: 'contacts:view',
  },
  {
    // The places we do work, sitting under the people who own them. Taken off
    // the sidebar in #2533 when it was a read-only list nothing pointed at;
    // back now that a location carries its own service and billing contacts
    // and decides who a job notifies. Same scope as Contacts — a location is
    // customer data, and the route guards on it too.
    label: 'Locations',
    path: '/dashboard/locations',
    icon: MapPin,
    aliases: ['sites', 'addresses', 'service addresses', 'branches'],
    requiredPermission: 'contacts:view',
  },
  {
    // Every customer's tasks in one place. Contacts → Tasks stays too; same
    // list, but this one is a destination with status tabs. Gate matches
    // GET /contact-tasks (contacts:view).
    label: 'Tasks',
    path: '/dashboard/tasks',
    icon: ListTodo,
    aliases: ['to-dos', 'todos', 'follow-ups', 'contact tasks'],
    requiredPermission: 'contacts:view',
  },
];

export const PINNED_SECTIONS: NavSection[] = PINNED_NAV_ITEMS.filter(
  (item): item is NavItem & { sectionId: string; children: NavItem[] } =>
    Boolean(item.sectionId && item.children?.length),
).map((item) => ({
  id: item.sectionId,
  label: item.label,
  icon: item.icon,
  items: item.children,
}));

// NOT-567 Cluster 2 (growth): the lead → estimate → proposal funnel (Sales),
// Marketing, and the new Iris AI section. Rendered above a divider from the
// back-office cluster.
export const GROWTH_SECTIONS: NavSection[] = [
  {
    id: 'sales',
    label: 'Sales',
    icon: UserPlus,
    items: [
      {
        // NOT-567 — Leads moves out of Contacts so the funnel lives in one place.
        label: 'Leads',
        path: '/dashboard/leads',
        icon: UserPlus,
        requiredPermission: 'leads:view',
      },
      {
        label: 'Estimates',
        path: '/dashboard/estimates',
        icon: FileText,
        requiredPermission: 'estimates:view',
      },
      {
        label: 'Proposals',
        path: '/dashboard/proposals',
        icon: FileStack,
        requiredPermission: 'estimates:view',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    items: [
      {
        label: 'Reviews',
        path: '/dashboard/reviews',
        icon: Star,
        // Route file (`reviews.tsx`) gates with `beforeLoad` on
        // `reviews:view` and redirects users without it home. Without
        // aligning the nav permission, a contacts:view-only operator
        // sees the link, clicks it, and is silently bounced to home
        // with no explanation. Mirrors the SMS/Phone permission
        // alignment done in #751 and #759.
        requiredPermission: 'reviews:view',
      },
      {
        label: 'Promotions',
        path: '/dashboard/promotions',
        icon: Megaphone,
        requiredPermission: 'reminders:view',
      },
      // NOT-567 — Reminders moved out of Marketing into Settings > Reminders
      // (Queue + Configuration tabs). /dashboard/reminders now redirects there.
    ],
  },
];

// Shown only to tenants with purchasing turned on (PUR-INV-105).
const PURCHASING_SECTION: NavSection = {
  id: 'purchasing',
  label: 'Purchasing',
  icon: Package,
  items: [
    // The purchasing overview page stays reachable by its address only; it is
    // not offered in the menu for now.
    {
      label: 'Purchase orders',
      path: '/dashboard/purchasing',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      icon: ClipboardList,
      requiredPermission: 'purchase_orders:view',
      aliases: ['procurement', 'receiving', 'purchase orders'],
    },
    {
      label: 'Approvals',
      path: '/dashboard/purchasing/approvals',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      icon: ClipboardCheck,
      requiredAnyPermission: ['purchase_orders:approve', 'supplier_bills:approve'],
      aliases: ['approve purchases', 'spending approvals'],
    },
    // Company cards is off the menu for now (owner request, 2026-09-27); the
    // route at /dashboard/purchasing/cards still works.
    {
      label: 'My purchases',
      path: '/dashboard/purchasing/field',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      icon: ShoppingCart,
      requiredPermission: 'field_purchases:create',
      aliases: ['buy parts', 'field purchase', 'counter purchase', 'receipt'],
    },
    {
      label: 'Suppliers',
      path: '/dashboard/purchasing/suppliers',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      icon: Building2,
      requiredPermission: 'suppliers:view',
      aliases: ['vendors', 'supply houses'],
    },
    {
      label: 'Cost changes',
      path: '/dashboard/purchasing/cost-changes',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      icon: TrendingUp,
      requiredPermission: 'suppliers:view',
      aliases: ['supplier costs', 'price file', 'cost review', 'proposed items'],
    },
  ],
};

// Stock, and where it is kept: its own section under Purchasing. What a
// reconciled delivery puts away shows here, in the location's stock and
// activity. Shown only to tenants with inventory turned on (PUR-INV-105).
const INVENTORY_SECTION: NavSection = {
  id: 'inventory',
  label: 'Inventory',
  icon: Boxes,
  items: [
    {
      label: 'Stock',
      path: '/dashboard/inventory',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: Boxes,
      requiredPermission: 'inventory:view',
      aliases: [
        'stock',
        'warehouses',
        'trucks',
        'jobs with material',
        'add warehouse',
        'add truck',
        'locations',
        'pick lists',
        'picking',
      ],
    },
    {
      label: 'Stock levels',
      path: '/dashboard/inventory/stock',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: ListChecks,
      requiredPermission: 'inventory:view',
      aliases: ['on hand', 'adjust stock', 'below minimum', 'stock levels'],
    },
    {
      label: 'Transfers',
      path: '/dashboard/inventory/transfers',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: ArrowLeftRight,
      requiredPermission: 'inventory:view',
      aliases: ['move stock', 'truck stock', 'transfer stock'],
    },
    {
      label: 'Counts',
      path: '/dashboard/inventory/counts',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: ListChecks,
      requiredPermission: 'inventory:view',
      aliases: ['stock count', 'cycle count', 'inventory count'],
    },
    {
      label: 'Replenishment',
      path: '/dashboard/inventory/replenishment',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: RefreshCw,
      requiredPermission: 'inventory:view',
      aliases: ['restock', 'reorder', 'below minimum'],
    },
    {
      label: 'My truck',
      path: '/dashboard/inventory/my-truck',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: Truck,
      requiredPermission: 'inventory:view',
      aliases: ['truck stock', 'what is on my truck'],
    },
    {
      label: 'Truck templates',
      path: '/dashboard/inventory/truck-templates',
      requiredFlag: PURCHASING_FLAGS.inventory,
      icon: LayoutTemplate,
      requiredPermission: 'inventory:edit',
      aliases: ['truck stock list', 'standard truck stock'],
    },
  ],
};

// NOT-567 Cluster 3 (back office): Billing, Team, and the new Resources group.
export const BACK_OFFICE_SECTIONS: NavSection[] = [
  PURCHASING_SECTION,
  INVENTORY_SECTION,
  {
    id: 'billing',
    label: 'Billing',
    icon: Receipt,
    // NOT-567 — Transactions/Recurring sub-headers dropped; the list reads
    // cleanly as one column. (Receipts → Payments and the Recovery & Renewals
    // split land in later phases of NOT-567.)
    items: [
      {
        label: 'Invoices',
        path: '/dashboard/invoices',
        icon: Receipt,
        requiredPermission: 'invoices:view',
      },
      {
        // Receipts are the records of payments and deposits. They are still
        // reachable from each payment/deposit detail, but the tenant-wide list
        // (filter by kind, method, number, date) is its own Billing page again.
        // Hidden for roles that cannot see amounts or payment details, since
        // every column on the list is one or the other.
        label: 'Receipts',
        path: '/dashboard/receipts',
        icon: ReceiptText,
        requiredPermission: 'payments:view',
        hiddenForFieldMasks: ['PRICING', 'PAYMENT_INFO'],
      },
      {
        label: 'Payments',
        path: '/dashboard/payments',
        icon: CreditCard,
        requiredPermission: 'payments:view',
      },
      {
        // NOT-567 — Deposits promoted to a first-class Billing page (split list +
        // table + detail), symmetric with Payments; a deposit's receipt lives on
        // its detail the same way a payment's does.
        label: 'Deposits',
        path: '/dashboard/deposits',
        icon: Wallet,
        requiredPermission: 'payments:view',
      },
      {
        label: 'Credit Memos',
        path: '/dashboard/credit-memos',
        icon: FileMinus2,
        requiredPermission: 'invoices:view',
      },
      {
        label: 'Recurring',
        path: '/dashboard/recurring-billing',
        icon: CalendarClock,
        aliases: ['recurring billing'],
        requiredPermission: 'contracts:view',
      },
      {
        // NOT-567 — the recovery/dunning half of the old "Recovery & Renewals"
        // workspace; the renewals half moved to a tab on Memberships.
        label: 'Past due',
        path: '/dashboard/past-due',
        icon: CalendarClock,
        aliases: ['recovery', 'dunning', 'recovery & renewals'],
        requiredPermission: 'billing:view',
      },
      {
        label: 'Memberships',
        path: '/dashboard/memberships',
        icon: CreditCard,
        aliases: ['renewals'],
        requiredPermission: 'memberships:view',
      },
      {
        // Recurring Service Events — the office booking queue for the included
        // visits a membership owes; each books into a $0 job. The tune-up
        // follow-up queue folded into this screen (outreach status + Needs-office
        // filter); the auto-schedule settings live behind its Settings toggle.
        label: 'Included visits',
        path: '/dashboard/membership-visits',
        icon: Repeat,
        aliases: ['recurring services', 'membership visits', 'covered visits', 'booking queue'],
        requiredPermission: 'memberships:view',
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    icon: Users2,
    items: [
      {
        label: 'Technicians',
        path: '/dashboard/technicians',
        icon: Users2,
        requiredPermission: 'technicians:view',
      },
      {
        label: 'Crews',
        path: '/dashboard/crews',
        icon: Users2,
        requiredPermission: 'technicians:view',
      },
      {
        label: 'Timesheets',
        path: '/dashboard/timesheet',
        icon: Clock,
        aliases: ['timesheet'],
        requiredPermission: 'timesheets:view',
      },
    ],
  },
  {
    // Equipment: the tenant's equipment domain in pipeline order — the trade
    // The physical units installed at customers. Equipment TYPES (make/model
    // specs) and their categories are no longer separate destinations: a type
    // is created with the pricebook equipment it prices, because keeping them
    // apart meant building the same equipment twice in two places.
    // The routes still exist for deep links; they are just not navigated to.
    id: 'equipment',
    label: 'Equipment',
    icon: Wrench,
    items: [
      {
        label: 'Equipment Inventory',
        path: '/dashboard/equipment',
        icon: Package,
        aliases: ['equipment registry', 'registry', 'installed equipment'],
        requiredPermission: 'equipment:view',
      },
    ],
  },
];

export const REPORTING_SECTIONS: NavSection[] = [
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    items: [
      {
        label: 'Reports',
        path: '/dashboard/reporting/reports',
        icon: FileStack,
        requiredPermission: 'reports:view',
      },
      {
        label: 'Scorecards',
        path: '/dashboard/reporting/scorecards',
        icon: Gauge,
        requiredPermission: 'reports:view',
      },
      {
        label: 'Targets',
        path: '/dashboard/reporting/targets',
        icon: BadgeCheck,
        requiredPermission: 'reports:view',
      },
      {
        label: 'Calls',
        // Points at the Action Needed report, which absorbed everything the
        // separate Call Report showed. Repointed rather than removed: the way
        // in from Reporting is the same, and a menu entry that disappears is
        // how somebody concludes the feature was taken away.
        path: '/dashboard/calllogs/reports',
        icon: PhoneCall,
        // Gated on call history, not `reports:view`: this reads the call log,
        // and someone who cannot open a call has no business reading a report
        // built out of them.
        requiredPermission: 'messages:view',
      },
    ],
  },
];

export const WORK_AREA_SECTIONS: NavSection[] = [...GROWTH_SECTIONS, ...BACK_OFFICE_SECTIONS];

export const NAV_SECTIONS: NavSection[] = [...WORK_AREA_SECTIONS, ...REPORTING_SECTIONS];

export const SETTINGS_ITEM: NavItem = {
  label: 'Settings',
  path: '/dashboard/settings',
  icon: Settings,
};

export const SETTINGS_SECTION: NavSection = {
  id: 'settings',
  label: 'Settings',
  icon: Settings,
  items: [
    // Account
    {
      label: 'Company Profile',
      path: '/dashboard/settings/company',
      icon: Building2,
      requiredPermission: 'company:view',
      group: 'Account',
    },
    {
      label: 'Users',
      path: '/dashboard/settings/users',
      icon: Users,
      requiredPermission: 'users:view',
      group: 'Account',
    },
    {
      label: 'Roles',
      path: '/dashboard/settings/permissions',
      icon: Shield,
      requiredPermission: 'roles:view',
      group: 'Account',
    },
    {
      label: 'Groups',
      path: '/dashboard/settings/groups',
      icon: Users,
      requiredPermission: 'users:view',
      group: 'Account',
    },
    {
      label: 'Subscription',
      path: '/dashboard/settings/subscription',
      icon: BadgeCheck,
      requiredPermission: 'billing:view',
      group: 'Account',
    },
    // Communications
    {
      label: 'Channels',
      path: '/dashboard/settings/channels',
      icon: Hash,
      // Viewing channels needs messages:view; managing them needs
      // channels:manage (enforced on the write endpoints).
      requiredPermission: 'messages:view',
      group: 'Communications',
    },
    {
      label: 'Phone',
      path: '/dashboard/settings/phone',
      icon: Phone,
      // Page reads phone numbers from /api/messaging/phone-numbers which
      // requires `messages:view` on the backend. Aligning the nav gate
      // with the BE permission so a `company:view`-only operator doesn't
      // get a misleading "No phone numbers provisioned yet" empty state.
      requiredPermission: 'messages:view',
      group: 'Communications',
    },
    {
      label: 'SMS',
      path: '/dashboard/settings/sms',
      icon: MessageSquare,
      // Page reads phone numbers from /api/messaging/phone-numbers, gated
      // by `messages:view` on the backend. Same alignment as the Phone
      // entry above — a `company:view`-only operator would otherwise see
      // a misleading "No SMS numbers provisioned yet" empty state caused
      // by a 403 the suppressed toast hides.
      requiredPermission: 'messages:view',
      group: 'Communications',
    },
    {
      label: 'Email accounts',
      path: '/dashboard/settings/email-accounts',
      icon: Mail,
      // No `requiredPermission` — same default behaviour as the
      // Notifications entry below. The page itself gates per-mailbox
      // actions through `messages:create` server-side. Wiring it
      // through the Settings nav so users don't have to type the URL
      // directly to reach the Gmail / Outlook connect flow.
      group: 'Communications',
    },
    {
      label: 'Notifications',
      path: '/dashboard/settings/notifications',
      icon: Bell,
      requiredPermission: 'notifications:view',
      group: 'Communications',
    },
    {
      label: 'Reminders',
      path: '/dashboard/settings/reminders',
      icon: Bell,
      aliases: ['reminders queue'],
      requiredPermission: 'reminders:view',
      group: 'Communications',
    },
    {
      label: 'Call Settings',
      path: '/dashboard/settings/call-settings',
      icon: PhoneCall,
      // Gate on `company:view`: most of the page (automation, call flows, AI
      // receptionist, and the overview) reads company-scoped config from the CS proxy. The
      // Agents tab additionally needs `users:view` — the page hides that tab and
      // skips its fetch when the user lacks it, so nothing 403s.
      requiredPermission: 'company:view',
      group: 'Communications',
    },
    // AI — Iris studio: train/test/deploy/analyze the AI service agent.
    {
      label: 'Iris',
      path: '/dashboard/settings/chatbot',
      icon: Sparkles,
      requiredPermission: 'ai_config:view',
      group: 'AI',
    },
    // AI — Otto: chat-with-your-data analytics agent (Sigma-style reports).
    {
      label: 'Otto',
      path: '/dashboard/ai/otto',
      icon: BarChart3,
      requiredPermission: 'otto:view',
      group: 'AI',
    },
    // Integrations
    {
      label: 'Connected Apps',
      path: '/dashboard/settings/integrations',
      icon: Plug,
      requiredPermission: 'integrations:view',
      group: 'Integrations',
    },
    {
      label: 'Accounting',
      path: '/dashboard/settings/accounting',
      icon: BookOpen,
      requiredPermission: 'integrations:view',
      group: 'Integrations',
    },
    {
      label: 'Webhooks',
      path: '/dashboard/settings/webhooks',
      icon: Plug,
      requiredPermission: 'company:view',
      group: 'Integrations',
    },
    {
      label: 'Partner API',
      path: '/dashboard/settings/api-keys',
      activePaths: ['/dashboard/settings/api-keys', '/dashboard/settings/partner-api/webhooks'],
      icon: KeyRound,
      requiredPermission: 'company:view',
      group: 'Integrations',
    },
    // Sales & billing
    {
      label: 'Estimate settings',
      path: '/dashboard/settings/estimates',
      icon: FileText,
      requiredPermission: 'company:view',
      group: 'Sales & billing',
    },
    {
      label: 'Client portal',
      path: '/dashboard/settings/client-portal',
      icon: Globe,
      // Mirror the API (GET is company:view OR company:edit) so an edit-only
      // admin still sees the item, matching useClientPortalSettings.
      requiredAnyPermission: ['company:view', 'company:edit'],
      group: 'Customers',
    },
    {
      // Gated on reports:view to match the operations API — GET /settings/reports
      // (and the templates subpage) require reports:view, so surfacing it to
      // company:view-only admins would land them on a 403.
      label: 'Report settings',
      path: '/dashboard/settings/reports',
      icon: ClipboardList,
      requiredPermission: 'reports:view',
      group: 'Sales & billing',
    },
    {
      label: 'Invoice settings',
      path: '/dashboard/settings/billing',
      icon: CreditCard,
      requiredPermission: 'billing:view',
      group: 'Sales & billing',
    },
    {
      label: 'Pricebook',
      path: '/dashboard/pricebook',
      icon: Package,
      requiredPermission: 'pricing:view',
      group: 'Sales & billing',
    },
    {
      label: 'Equipment categories',
      path: '/dashboard/equipment-categories',
      icon: Wrench,
      requiredPermission: 'equipment:view',
      group: 'Sales & billing',
    },
    {
      label: 'Taxes',
      path: '/dashboard/taxes',
      icon: Receipt,
      requiredPermission: 'company:view',
      group: 'Sales & billing',
    },
    {
      label: 'Templates',
      path: '/dashboard/settings/templates',
      icon: LayoutTemplate,
      requiredPermission: 'estimates:view',
      group: 'Sales & billing',
    },
    {
      label: 'Document designs',
      path: '/dashboard/settings/document-templates',
      icon: Paintbrush,
      requiredPermission: 'estimates:view',
      group: 'Sales & billing',
    },
    {
      // Suppliers serve expenses and the price book too, so this directory
      // stays reachable whether or not the tenant has purchasing.
      label: 'Suppliers',
      path: '/dashboard/settings/suppliers',
      icon: Building2,
      requiredPermission: 'suppliers:view',
      group: 'Sales & billing',
      aliases: ['vendors', 'supplier directory'],
    },
    {
      label: 'Purchasing',
      path: '/dashboard/settings/purchasing',
      icon: Package,
      requiredPermission: 'purchasing:configure',
      requiredFlag: PURCHASING_FLAGS.purchaseOrders,
      group: 'Sales & billing',
    },
    {
      label: 'Terms & conditions',
      path: '/dashboard/settings/terms-and-conditions',
      icon: FileText,
      requiredPermission: 'estimates:view',
      group: 'Sales & billing',
    },
    {
      label: 'Scope of work',
      path: '/dashboard/settings/scope-of-work',
      icon: ClipboardList,
      requiredPermission: 'estimates:view',
      group: 'Sales & billing',
    },
    {
      label: 'Membership plans',
      path: '/dashboard/membership-plans',
      icon: BadgeCheck,
      requiredPermission: 'pricing:view',
      group: 'Sales & billing',
    },
    {
      label: 'Payroll profiles',
      path: '/dashboard/settings/payroll-profiles',
      icon: Wallet,
      requiredPermission: 'payroll:view',
      group: 'Sales & billing',
    },
    // Operations
    {
      label: 'Scheduling',
      path: '/dashboard/settings/office-settings',
      icon: CalendarClock,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      // Scheduling v2 — booking policy + arrival-window presets. Gated on the
      // policy's own view permission (GET /v2/scheduling/policy), not
      // company:view, so a dispatcher-admin without company settings still
      // reaches it and a company-only admin is not sent to a 403.
      label: 'Bookings',
      path: '/dashboard/settings/booking-windows',
      icon: Timer,
      requiredPermission: 'scheduling_policy:view',
      group: 'Operations',
    },
    {
      label: 'On-call',
      path: '/dashboard/settings/oncall',
      icon: PhoneCall,
      requiredPermission: 'availability:view',
      group: 'Operations',
    },
    {
      label: 'Job Types',
      path: '/dashboard/settings/jobtypes',
      icon: Briefcase,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      label: 'Recurring Jobs',
      path: '/dashboard/settings/contracts',
      icon: CalendarClock,
      requiredPermission: 'contracts:view',
      group: 'Operations',
    },
    {
      label: 'Reviews',
      path: '/dashboard/settings/reviews',
      icon: Star,
      // NOT-576 — this screen now carries the review request rules, the
      // `REVIEW_REQUEST` reminder policy that used to be reached through
      // Settings > Reminders, and the platform connections that used to be
      // Settings > Review Platforms. The gate is the union of the three tabs'
      // own view gates, so the entry is visible exactly when at least one tab
      // is: `contracts:view` opens Request rules (`canViewRequestRules`), the
      // `reminders:view | reminders:edit | company:view` trio opens Reminder
      // policy (`canViewPolicy`), and `reviews:view` opens Platforms
      // (`canViewPlatforms`) — all three in `settings/reviews.tsx`. Keep this
      // list and those two hooks in step: a permission here that opens no tab
      // lands its holder on an empty screen, and one the screen serves but this
      // list omits leaves that cohort able to read the policy by URL with no way
      // to click to it. The trio is wider than the old Reminders *nav* gate
      // (`reminders:view`) on purpose — an edit-only or company-only operator
      // reached that screen through the ungated "View all steps" links on the
      // appointment panels, so the click path existed before this move.
      requiredAnyPermission: ['contracts:view', ...REVIEW_POLICY_VIEW_PERMISSIONS, 'reviews:view'],
      group: 'Operations',
    },
    {
      label: 'Online Booking',
      path: '/dashboard/settings/online-booking',
      icon: Globe,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      label: 'Job Pipelines',
      path: '/dashboard/settings/job-pipelines',
      icon: GitBranch,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      label: 'Custom Fields',
      path: '/dashboard/settings/customfields',
      icon: ListChecks,
      requiredPermission: 'custom_fields:view',
      group: 'Operations',
    },
    {
      label: 'Forms',
      path: '/dashboard/settings/appointment-forms',
      icon: ClipboardList,
      requiredPermission: 'forms:view',
      group: 'Operations',
    },
    {
      label: 'Skills',
      path: '/dashboard/settings/skills',
      icon: Wrench,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      label: 'Trades & Business Units',
      path: '/dashboard/settings/trades',
      icon: Tags,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    {
      // Moved from CRM → Operations: it's scheduling config (territories +
      // capacity gate technician eligibility), not a CRM concern.
      label: 'Service Areas',
      path: '/dashboard/settings/service-areas',
      icon: MapPin,
      requiredPermission: 'company:view',
      group: 'Operations',
    },
    // CRM
    {
      label: 'Contact Statuses',
      path: '/dashboard/settings/contact-statuses',
      icon: Users,
      requiredPermission: 'company:view',
      group: 'CRM',
    },
    {
      label: 'Tags',
      path: '/dashboard/settings/tags',
      icon: Tag,
      requiredPermission: 'tags:view',
      group: 'CRM',
    },
    {
      label: 'Leads',
      path: '/dashboard/settings/leads',
      icon: UserPlus,
      requiredPermission: 'leads:view',
      group: 'CRM',
    },
    // System — must remain the last group so it renders at the bottom of the
    // Settings panel (SecondaryPanel emits group headers in array order).
    {
      label: 'Audit Logs',
      path: '/dashboard/audit',
      icon: ShieldCheck,
      requiredPermission: 'audit:view',
      group: 'System',
    },
  ],
};

export const PATH_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  contacts: 'Contacts',
  equipment: 'Equipment',
  assets: 'Assets',
  leads: 'Leads',
  invoices: 'Invoices',
  pricebook: 'Pricebook',
  purchasing: 'Purchase orders',
  'membership-plans': 'Membership Plans',
  inbox: 'Inbox',
  calllogs: 'Calls',
  'live-calls': 'Live calls',
  'crew-members': 'Crew Members',
  crewmember: 'Crew Member',
  timesheet: 'Timesheets',
  forms: 'Forms',
  reviews: 'Reviews',
  settings: 'Settings',
  templates: 'Templates',
  company: 'Company Profile',
  users: 'Users',
  billing: 'Invoice settings',
  taxes: 'Taxes',
  permissions: 'Roles',
  groups: 'Groups',
  bookings: 'Requests',
  'service-calls': 'Service calls',
  memberships: 'Memberships',
  reports: 'Reports',
  costing: 'Costing',
  unscheduled: 'Schedule review',
  board: 'Board',
  dispatch: 'Dispatch',
  calendar: 'Calendar',
  jobform: 'Forms',
  'appointment-forms': 'Forms',
  timeline: 'Timeline',
  home: 'Home',
  payments: 'Payments',
  receipts: 'Receipts',
  'recurring-billing': 'Recurring',
  'recovery-renewals': 'Recovery & Renewals',
  'past-due': 'Past due',
  chatbot: 'Chatbot Simulator',
  promotions: 'Promotions',
  reminders: 'Reminders',
  technicians: 'Technicians',
  crews: 'Crews',
  audit: 'Audit Logs',
  ai: 'AI',
  conversations: 'Conversations',
  'contact-statuses': 'Contact Statuses',
  webhooks: 'Webhooks',
  new: 'New',
  view: 'View',
};

/**
 * Route-aware breadcrumb overrides for Settings pages whose last path segment
 * collides with a different page. `PATH_LABELS` keys on the bare segment, so
 * `/dashboard/settings/chatbot` (AI Chatbot) would otherwise inherit the
 * `chatbot` → "Chatbot Simulator" label used by `/dashboard/chatbot`.
 */
export const FULL_PATH_LABELS: Record<string, string> = {
  '/dashboard/settings/chatbot': 'Iris',
};

/**
 * True when `pathname` belongs to the Settings section — either under the
 * `/dashboard/settings` prefix or any `SETTINGS_SECTION` item that points at a
 * route outside it (Pricebook, Taxes, Audit Logs, Chatbot Simulator, …). Use
 * this for "Settings active" nav state instead of hardcoding the path prefix,
 * so every Settings item lights up the Settings icon regardless of its URL.
 */
export function isSettingsPath(pathname: string): boolean {
  if (pathname === SETTINGS_ITEM.path || pathname.startsWith(SETTINGS_ITEM.path + '/')) {
    return true;
  }
  return SETTINGS_SECTION.items.some(
    (item) => pathname === item.path || pathname.startsWith(item.path + '/'),
  );
}

/** Given a pathname, return the section ID that contains a matching nav item. */
export function getSectionForPath(pathname: string): string | null {
  for (const section of PINNED_SECTIONS) {
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return section.id;
      }
      if (item.children) {
        for (const child of item.children) {
          const childPath = child.path.split('?')[0];
          if (pathname === childPath || pathname.startsWith(childPath + '/')) {
            return section.id;
          }
        }
      }
    }
  }

  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return section.id;
      }
      if (item.children) {
        for (const child of item.children) {
          const childPath = child.path.split('?')[0];
          if (pathname === childPath || pathname.startsWith(childPath + '/')) {
            return section.id;
          }
        }
      }
    }
  }
  if (pathname === SETTINGS_ITEM.path || pathname.startsWith(SETTINGS_ITEM.path + '/')) {
    return 'settings';
  }
  // Some settings items live outside /dashboard/settings (e.g. Pricebook,
  // Taxes, Membership plans moved into the Sales & billing group). Match them
  // so the Settings section stays active for those paths.
  for (const item of SETTINGS_SECTION.items) {
    if (pathname === item.path || pathname.startsWith(item.path + '/')) {
      return 'settings';
    }
  }
  return null;
}
